import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Box, Checkbox, FormGroup, Text } from 'tombac';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * MultiSelectLimitEditor Editor for MULTISELECT limit types. Renders a list of
 * checkboxes for options from schema.properties.options. Supports multiple
 * selections, default values, and fallback UI.
 */
export default function MultiSelectLimitEditor({
  className,
  defaultLimit,
  isSharedWithOther = false,
  label,
  limit,
  onChange,
  schema
}) {
  // Extract options from schema
  const options = useMemo(() => {
    if (schema && (schema.type === 'MULTISELECT' || schema.type === 'SELECT') && Array.isArray(schema.properties?.options)) {
      return schema.properties.options;
    }
    return [];
  }, [schema]);

  // Compute initial selected values: prefer limit, then defaultLimit, then schema option defaults, then empty array
  const initialSelected = useMemo(() => {
    if (Array.isArray(limit)) return limit;
    if (Array.isArray(defaultLimit)) return defaultLimit;
    if (options.length > 0) {
      return options.filter(opt => opt.default).map(opt => opt.value);
    }
    return [];
  }, [limit, defaultLimit, options]);

  // State for selected values
  const [selected, setSelected] = useState(initialSelected);

  // Keep state in sync with limit prop
  useEffect(() => {
    if (Array.isArray(limit)) {
      setSelected(limit);
    }
  }, [limit]);

  // Handle checkbox toggle
  const handleToggle = value => {
    let newSelected;
    if (selected.includes(value)) {
      newSelected = selected.filter(v => v !== value);
    } else {
      newSelected = [...selected, value];
    }
    setSelected(newSelected);
    onChange?.(newSelected);
  };

  // Fallback UI if options are missing or malformed
  if (!Array.isArray(options) || options.length === 0) {
    return /*#__PURE__*/_jsx(FormGroup, {
      className: className,
      label: label,
      $width: "100%",
      children: /*#__PURE__*/_jsx(Text, {
        $color: "neutral.500",
        fontSize: 14,
        children: "No options available"
      })
    });
  }
  return /*#__PURE__*/_jsx(FormGroup, {
    className: className,
    label: label,
    $width: "100%",
    "data-sentry-element": "FormGroup",
    "data-sentry-component": "MultiSelectLimitEditor",
    "data-sentry-source-file": "MultiSelectLimitEditor.tsx",
    children: /*#__PURE__*/_jsx(OptionList, {
      "data-sentry-element": "OptionList",
      "data-sentry-source-file": "MultiSelectLimitEditor.tsx",
      "data-sentry-element": "OptionList",
      children: options.map(option => /*#__PURE__*/_jsx(Box, {
        as: "li",
        children: /*#__PURE__*/_jsx(Checkbox, {
          checked: selected.includes(option.value),
          disabled: isSharedWithOther,
          label: option.label ?? String(option.value),
          onChange: () => handleToggle(option.value),
          value: option.value
        })
      }, option.value))
    })
  });
}
const OptionList = styled.ul.withConfig({
  displayName: "MultiSelectLimitEditor__OptionList"
})(["list-style:none;padding:0;margin:0;"]);